import type { ComponentPublicInstance, InjectionKey, Ref } from 'vue'
import { inject, onBeforeUnmount, onMounted } from 'vue'

export type RefreshHandler = () => unknown

export interface RefreshTarget {
  element: () => Element | null
  refresh: RefreshHandler
}

export interface RefreshRegistry {
  register: (target: RefreshTarget) => () => void
}

export const refreshRegistryKey: InjectionKey<RefreshRegistry> = Symbol('pull-to-refresh')

/**
 * Lets a screen refresh its own data when the user pulls down. The screen
 * names its element so a pull lands on the pane under the finger when several
 * panes are on screen. Screens that never call this get no pull at all.
 */
export function usePullToRefresh(element: Ref<Element | ComponentPublicInstance | null>, refresh: RefreshHandler): void {
  const registry = inject(refreshRegistryKey, null)
  if (!registry) return

  let unregister: (() => void) | undefined
  onMounted(() => {
    unregister = registry.register({
      element: () => {
        const value = element.value
        if (value instanceof Element) return value
        return value?.$el instanceof Element ? value.$el : null
      },
      refresh,
    })
  })
  onBeforeUnmount(() => unregister?.())
}

/**
 * The innermost screen whose element holds the touch. A lone screen also takes
 * touches outside its element, such as on the layout's filter bar above it.
 */
export function pickTarget(targets: readonly RefreshTarget[], node: Node): RefreshTarget | undefined {
  let picked: { target: RefreshTarget; element: Element } | undefined
  for (const target of targets) {
    const element = target.element()
    if (!element?.contains(node)) continue
    if (!picked || picked.element.contains(element)) picked = { target, element }
  }
  if (picked) return picked.target
  return targets.length === 1 ? targets[0] : undefined
}

/** True when nothing between the touch and the page root is scrolled down. */
export function isScrolledToTop(node: Element): boolean {
  for (let element: Element | null = node; element; element = element.parentElement) {
    if (element.scrollTop >= 1) return false
  }
  return true
}

/**
 * Surfaces that use a vertical drag themselves: maps, drawing canvases such as
 * signature pads, and sliders. A screen can opt out any element with the
 * `data-no-pull-refresh` attribute.
 */
const OWN_DRAG_SELECTOR = '[data-no-pull-refresh], .gm-style, canvas, .q-slider, .q-range'

export function ownsVerticalDrag(node: Element): boolean {
  return node.closest(OWN_DRAG_SELECTOR) !== null
}

/**
 * Resistance like iOS's rubber band: the pull follows the finger at first and
 * stiffens toward `max`, which it never reaches.
 */
export function rubberBand(travel: number, max: number): number {
  if (travel <= 0) return 0
  return (travel * max) / (travel + max)
}
